import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { tokens } from '../../public/tokens';

const { colors, typography } = tokens;

/**
 * How long the button shows its "copied" tick before reverting.
 */
const COPIED_FEEDBACK_MS = 1000;

const CopyIcon = styled.button({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    color: colors.states.info['500'],
    fontSize: typography.fontSize['base-16px'],
    fontWeight: typography.fontWeights.light,
});

const Row = styled.div({
    position: 'relative',
    width: '100%',
});

const Content = styled.div({
    width: '100%',
});

/**
 * Overlays the copy button on the right edge of the content it belongs to.
 * @param {string} $offset Inset from the content's own right edge
 */
const CopyOverlay = styled.div(({ $offset }) => ({
    position: 'absolute',
    right: $offset,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
}));

/**
 * Self-contained click-to-copy button. Feedback state is local to each
 * instance, so multiple CopyButtons on the same page can each show their
 * own "copied" state independently.
 */
function CopyButton({ text, title = 'Copy' }) {
    const [copied, setCopied] = useState(false);
    const revertTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (revertTimerRef.current) {
                clearTimeout(revertTimerRef.current);
            }
        };
    }, []);

    const handleClick = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);

            // Cleared first so repeated clicks reset the window rather than stacking timers.
            if (revertTimerRef.current) {
                clearTimeout(revertTimerRef.current);
            }

            revertTimerRef.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
        } catch (error) {
            console.log('<CopyableField.js>-<handleClick>-Error', error);
        }
    };

    return (
        <CopyIcon type="button" onClick={handleClick} title={title} aria-label={title}>
            <i className={copied ? 'fas fa-check' : 'fal fa-copy'}></i>
        </CopyIcon>
    );
}

/**
 * Wraps arbitrary content (text, an input, ...) with a copy button overlaid on its
 * right edge, as if the button were part of the content. Hidden when there's nothing
 * to copy or `enableCopy` is false, so it never covers a field's invalid indicator.
 * @param {string} [copyButtonOffset] Inset from the content's right edge. Default
 * 12px suits unpadded content; pass '0' inside an already-padded container.
 */
function CopyableField({ children, textToCopy, copyTitle = 'Copy', copyButtonOffset = '12px', enableCopy = true }) {
    const showCopyButton = !!textToCopy && enableCopy;

    return (
        <Row>
            <Content>{children}</Content>
            {showCopyButton && (
                <CopyOverlay $offset={copyButtonOffset}>
                    <CopyButton text={textToCopy} title={copyTitle} />
                </CopyOverlay>
            )}
        </Row>
    );
}

export default CopyableField;
